import * as React from 'react';
import { makeStyles, createStyles, Box, Icon, Typography } from '@material-ui/core';
import cn from 'classnames';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@material-ui/icons';
import { TranquiTheme } from '../../containers/App/theme';
import { BankType, NegotiationOption, NegotiationOptionAlternative, NegotiationType, OutDebt } from '../../generated/graphql';
import RenderText from '../../dictionaries/RenderText';
import useDictionary from '../../dictionaries/context/useDictionary';
import { getExtraN26Suffix } from '../../utils/getExtraN26Suffix';
import { useSpace } from '../../content/utils/useSpace';

interface AccordionNegOptionsCardTitleProps {
  negOption: NegotiationOption;
  alternative: NegotiationOptionAlternative;
  isSelected: boolean;
  withExpandButton?: boolean;
  outDebt?: OutDebt;
}

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  itemHeaderContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemHeaderBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    columnGap: `${theme.spacing(2)}px`,
  },
  itemHeaderIcon: {
    color: theme.palette.indigo.main,
  },
  title: {
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.indigo.main,
    textAlign: 'left',
  },
  down: {
    color: theme.palette.indigo.main,
    transition: 'all .4s',
  },
}));

const AccordionNegOptionsCardTitle: React.FC<AccordionNegOptionsCardTitleProps> = (props) => {
  const classes = useStyles();
  const { negOption, isSelected, withExpandButton, outDebt, alternative } = props;
  const { gT } = useDictionary();

  const suffix = getExtraN26Suffix(outDebt, negOption);
  const isBogotaBalanceBehindPayment = outDebt?.bank === BankType.BancoDeBogota
  && negOption.type === NegotiationType.BalanceBehindPayment;
  const bogotaBalanceBehindPaymentSubType = isBogotaBalanceBehindPayment && alternative?.type
    ? `_${alternative.type}`
    : '';
  const titleKey = `negotiation_${negOption.type}${suffix}_label${bogotaBalanceBehindPaymentSubType}`;

  // space implementation
  const spaceContext = {
    outDebt,
    option: negOption,
    alternative,
  };
  const negOptionTitleSpace = useSpace('NEG_OPTION', 'title', spaceContext);

  return (
    <Box className={cn(classes.itemHeaderContent, !!gT(`negotiation_${negOption.type}_description`) && 'mb-1')}>
      <Box className={classes.itemHeaderBox}>
        <Icon className={classes.itemHeaderIcon}>{gT(`negotiation_${negOption.type}_icon`)}</Icon>
        <Typography className={classes.title}>
          <RenderText
            textKey={negOptionTitleSpace.keyIf || titleKey}
            isMarkdown
          />
        </Typography>
      </Box>
      {withExpandButton
      && (
      <ExpandMoreRoundedIcon
        className={classes.down}
        style={isSelected ? { transform: 'rotate(180deg)' } : undefined}
      />
      )}
    </Box>
  );
};

export default AccordionNegOptionsCardTitle;
